import React, { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { BellRinging, CheckCircle, XCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

function useAlarm(on) {
  const ref = useRef(null);
  useEffect(() => {
    if (!on) return undefined;
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const beep = () => {
      ctx.resume().catch(() => {});
      [0, 0.28, 0.56].forEach((t) => {
        const o = ctx.createOscillator(); const g = ctx.createGain();
        o.type = "square"; o.frequency.setValueAtTime(t === 0.28 ? 660 : 880, ctx.currentTime + t);
        g.gain.setValueAtTime(0.0001, ctx.currentTime + t); g.gain.exponentialRampToValueAtTime(0.9, ctx.currentTime + t + 0.02); g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + t + 0.24);
        o.connect(g).connect(ctx.destination); o.start(ctx.currentTime + t); o.stop(ctx.currentTime + t + 0.25);
      });
    };
    beep();
    ref.current = setInterval(beep, 2500);
    return () => { clearInterval(ref.current); ctx.close().catch(() => {}); };
  }, [on]);
}

export function DispatchCheckGate() {
  const [due, setDue] = useState(null);
  const [ans, setAns] = useState({});
  const [busy, setBusy] = useState(false);
  useAlarm(!!due);
  useEffect(() => {
    let alive = true;
    const poll = () => api.get("/dispatch-checks/due").then(({ data }) => { if (alive) setDue((cur) => (data.due ? cur || data : null)); }).catch(() => {});
    poll();
    const id = setInterval(poll, 30000);
    return () => { alive = false; clearInterval(id); };
  }, []);
  useEffect(() => {
    if (!due) return undefined;
    const block = (e) => { if (e.key === "Escape") e.preventDefault(); };
    window.addEventListener("keydown", block, true);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", block, true); document.body.style.overflow = ""; };
  }, [due]);
  if (!due) return null;
  const qs = due.questions;
  const ready = qs.every((q) => ans[q] && (ans[q].yes || (ans[q].reason || "").trim().length >= 3));
  const set = (q, v) => setAns({ ...ans, [q]: { ...(ans[q] || {}), ...v } });
  const submit = async () => {
    setBusy(true);
    try { await api.post("/dispatch-checks", { answers: qs.map((q) => ({ question: q, yes: ans[q].yes, reason: ans[q].reason || "" })) }); toast.success("Thanks — check recorded"); setDue(null); setAns({}); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[9999] bg-[#07090f]/95 backdrop-blur-md flex items-center justify-center p-4" role="alertdialog" aria-modal="true" data-testid="dispatch-check-gate">
      <div className="w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-3xl border border-red-500/50 bg-[#0b0f19] p-6 sm:p-8 shadow-[0_0_80px_rgba(239,68,68,0.35)]">
        <div className="flex items-center gap-3"><span className="h-14 w-14 rounded-2xl bg-red-500/15 grid place-items-center animate-pulse"><BellRinging size={30} weight="fill" className="text-red-500" /></span>
          <div><p className="text-[11px] font-bold uppercase tracking-[0.25em] text-red-400">30-minute dispatch check</p><h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-white">Answer to continue working</h2></div></div>
        <div className="mt-6 space-y-3">{qs.map((q, i) => {
          const a = ans[q];
          return (
            <div key={q} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid={`dc-q-${i}`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3"><p className="text-base font-semibold text-white">{q}</p>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => set(q, { yes: true })} className={`h-10 px-5 rounded-xl text-sm font-bold inline-flex items-center gap-1.5 transition-colors ${a?.yes === true ? "bg-emerald-500 text-slate-950" : "bg-white/5 text-slate-300 hover:bg-white/10"}`} data-testid={`dc-yes-${i}`}><CheckCircle size={17} weight="fill" />Yes</button>
                  <button onClick={() => set(q, { yes: false })} className={`h-10 px-5 rounded-xl text-sm font-bold inline-flex items-center gap-1.5 transition-colors ${a?.yes === false ? "bg-red-500 text-white" : "bg-white/5 text-slate-300 hover:bg-white/10"}`} data-testid={`dc-no-${i}`}><XCircle size={17} weight="fill" />No</button>
                </div></div>
              {a?.yes === false && <Textarea rows={2} value={a.reason || ""} onChange={(e) => set(q, { reason: e.target.value })} placeholder="Why not? (required — your admin will see this)" className="mt-3 bg-white/5 border-white/10 text-white" data-testid={`dc-reason-${i}`} />}
            </div>);
        })}</div>
        <Button onClick={submit} disabled={!ready || busy} className="mt-6 w-full h-12 bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold text-base" data-testid="dc-submit">{busy ? "Saving…" : "Submit & unlock screen"}</Button>
      </div>
    </div>
  );
}
